/*
 * Foto → 3D beeldje (Meshy.ai) — widget voor sections/meshy-3d.liquid
 *
 * Flow: foto('s) kiezen → inkaderen → /upload-image → /image-to-3d (de backend maakt
 * daar zelf een schone referentie van) → pollen op /image-to-3d-status → GLB tonen in
 * <model-viewer> → /cart/add.js.
 *
 * Eén foto is genoeg; de klant mag tot vier hoeken van hetzelfde onderwerp
 * meesturen, wat een gedetailleerder model oplevert (Meshy multi-image).
 *
 * Standen (root data-state): empty | chosen | cropping | working | done.
 * Elementen met [data-show="<stand>"] zijn alleen in die stand zichtbaar.
 */
(function () {
  'use strict';

  var POLL_INTERVAL_MS = 4000;
  var MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
  var MAX_FILE_BYTES = 40 * 1024 * 1024;
  var MAX_PHOTOS = 4;
  // Onder deze bronresolutie blijft er te weinig detail over voor een goed beeldje.
  var MIN_CROP_PX = 450;
  // Het uitsnedekader neemt de verhouding van de foto over, maar niet grenzeloos:
  // FLUX Kontext werkt tussen ongeveer 3:7 en 7:3, en van een panorama valt geen
  // beeldje te maken. Buiten deze grenzen knijpen we het kader terug.
  var MAX_CROP_RATIO = 1.6;
  var KIND_LABELS = { persoon: 'Persoon(en)', dier: 'Dier(en)', object: 'Object' };

  // Rem op het aantal keer dat iemand kan genereren. Een 3D-model kost credits, dus
  // twee per uur per browser. De voorstellen zijn goedkoper en mogen wat vaker.
  var LIMITS = {
    model: { key: 'm3d_models', max: 2, windowMs: 60 * 60 * 1000, wat: '3D-beeldjes' }
  };

  function usageLog(limit) {
    try {
      var raw = window.localStorage.getItem(limit.key);
      var list = raw ? JSON.parse(raw) : [];
      var since = Date.now() - limit.windowMs;
      return list.filter(function (t) { return typeof t === 'number' && t > since; });
    } catch (e) {
      return []; // geen opslag beschikbaar (privémodus): dan niet tegenhouden
    }
  }

  function usageNote(limit) {
    try {
      var list = usageLog(limit);
      list.push(Date.now());
      window.localStorage.setItem(limit.key, JSON.stringify(list));
    } catch (e) { /* niet kunnen opslaan is geen reden om te blokkeren */ }
  }

  // Geeft null als het mag, anders het aantal minuten dat de klant moet wachten.
  function usageWait(limit) {
    var list = usageLog(limit);
    if (list.length < limit.max) return null;
    var oldest = Math.min.apply(null, list);
    return Math.max(1, Math.ceil((oldest + limit.windowMs - Date.now()) / 60000));
  }

  // Vercel weigert requests boven ~4,5 MB (FUNCTION_PAYLOAD_TOO_LARGE). Een foto
  // rechtstreeks als base64 versturen mislukt dus bij vrijwel elke telefoonfoto.
  // Daarom verkleinen we hem eerst in de browser; Meshy heeft aan deze resolutie
  // ruim genoeg voor een goed 3D-model.
  var MAX_UPLOAD_PX = 1600;
  var UPLOAD_QUALITY = 0.88;
  var MAX_UPLOAD_CHARS = 3.2 * 1024 * 1024;

  var COLORS = {
    wit: { label: 'Wit', rgb: [0.95, 0.95, 0.95] },
    zwart: { label: 'Zwart', rgb: [0.07, 0.07, 0.08] }
  };
  var DEFAULT_COLOR = 'wit';

  function ensureModelViewer() {
    if (window.customElements && window.customElements.get('model-viewer')) return;
    if (document.querySelector('script[data-meshy-mv]')) return;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = MODEL_VIEWER_SRC;
    s.setAttribute('data-meshy-mv', '1');
    document.head.appendChild(s);
  }

  function init(root) {
    if (root.dataset.meshyInit === '1') return;
    root.dataset.meshyInit = '1';

    var cfg = {
      apiBase: (root.dataset.apiBase || '').replace(/\/$/, ''),
      widgetToken: root.dataset.widgetToken || '',
      pollMaxMs: (parseInt(root.dataset.pollMinutes, 10) || 8) * 60 * 1000
    };

    var els = {
      file: root.querySelector('.m3d__file'),
      stage: root.querySelector('.m3d__stage'),
      sourcePreview: root.querySelector('.m3d__source-preview'),
      progressBar: root.querySelector('.m3d__progress-bar'),
      progressText: root.querySelector('.m3d__progress-text'),
      viewer: root.querySelector('.m3d__viewer'),
      status: root.querySelector('.m3d__status'),
      error: root.querySelector('.m3d__error'),
      addBtn: root.querySelector('.m3d__add'),
      resetBtn: root.querySelector('.m3d__reset'),
      thumbs: root.querySelector('.m3d__thumbs'),
      cropFrame: root.querySelector('.m3d__crop-frame'),
      cropImg: root.querySelector('.m3d__crop-img'),
      cropRange: root.querySelector('.m3d__crop-range'),
      cropZoomBtns: root.querySelectorAll('.m3d__crop-zoom-btn'),
      cropOk: root.querySelector('.m3d__crop-ok'),
      cropCount: root.querySelector('.m3d__crop-count'),
      cropWarn: root.querySelector('.m3d__crop-warn'),
      layout: root.querySelector('.m3d__layout'),
      preview: root.querySelector('.m3d__preview'),
      sidebar: root.querySelector('.m3d__sidebar'),
      choicesCard: root.querySelector('.m3d__card--choices'),
      clearBtns: root.querySelectorAll('.m3d__clear'),
      subjectBtns: root.querySelectorAll('.m3d__subject'),
      kindBtns: root.querySelectorAll('.m3d__kind'),
      scale: root.querySelector('.m3d__scale'),
      sizeAdvice: root.querySelector('.m3d__size-advice'),
      generateBtn: root.querySelector('.m3d__generate'),
      clearBtn: root.querySelector('.m3d__clear'),
      colorBtns: root.querySelectorAll('.m3d__color'),
      variantInputs: root.querySelectorAll('.m3d__variant-input'),
      price: root.querySelector('.m3d__price'),
      priceCompare: root.querySelector('.m3d__price-compare'),
      giftCheck: root.querySelector('.m3d__gift-check')
    };

    var state = {
      // Gekozen foto's: 1 is genoeg, tot 4 hoeken geeft een gedetailleerder model.
      photos: [],
      photoUrls: [],
      // Bewust niet voorgeselecteerd. Een default van 1 haalde bij een foto van
      // twee personen de tweede persoon weg, omdat de prompt het aantal afdwingt.
      subjects: null,
      // Meerdere soorten tegelijk (persoon + dier): een foto van een baasje met
      // zijn hond heeft anders geen juiste keuze, en de prompt kreeg dan geen
      // enkele regel mee die de hond een hond liet blijven.
      kinds: [],
      stlUrl: null,
      glbUrl: null,
      thumbUrl: null,
      photoUrl: null,
      referenceUrl: null,
      busy: false,
      color: DEFAULT_COLOR,
      variantId: parseInt(root.dataset.variantId, 10) || 0,
      variantTitle: '',
      progressTarget: 0,
      progressShown: 0,
      progressTimer: null
    };

    ensureModelViewer();

    /* ---------- volgorde op mobiel ---------- */

    // Op een breed scherm staat de preview links en de sidebar rechts, dus alles is
    // in beeld. Op een telefoon staat alles onder elkaar en stond het preview-vlak
    // bovenaan, terwijl de knop om te beginnen onderaan zit: je tikte op de knop en
    // zag niets gebeuren. Daarom verhuist het vlak op mobiel naar de sidebar, direct
    // achter de vraag wat er op de foto staat.
    var mobiel = window.matchMedia('(max-width: 880px)');

    function arrangeLayout() {
      if (!els.preview || !els.sidebar || !els.choicesCard || !els.layout) return;
      if (mobiel.matches) {
        if (els.preview.parentElement !== els.sidebar) {
          els.choicesCard.insertAdjacentElement('afterend', els.preview);
        }
      } else if (els.preview.parentElement !== els.layout) {
        els.layout.insertAdjacentElement('afterbegin', els.preview);
      }
    }

    arrangeLayout();
    if (mobiel.addEventListener) mobiel.addEventListener('change', arrangeLayout);
    else if (mobiel.addListener) mobiel.addListener(arrangeLayout);

    /* ---------- stand & meldingen ---------- */

    function setState(next) {
      var vorige = root.dataset.state;
      root.dataset.state = next;
      root.querySelectorAll('[data-show]').forEach(function (el) {
        el.hidden = el.dataset.show !== next;
      });
      // Zonder dit tikt de klant op een knop en gebeurt er in beeld niets, omdat het
      // vlak zich buiten het zicht bevindt.
      if (vorige !== next && next !== 'empty') scrollStageInView();
    }

    function scrollStageInView() {
      if (!els.stage || !els.stage.scrollIntoView) return;
      var rect = els.stage.getBoundingClientRect();
      var buitenBeeld = rect.top < 0 || rect.bottom > window.innerHeight;
      if (!buitenBeeld) return;
      try {
        els.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        els.stage.scrollIntoView();
      }
    }

    function setStatus(msg) {
      if (els.status) els.status.textContent = msg || '';
    }

    function showError(msg) {
      if (els.error) {
        els.error.textContent = msg;
        els.error.hidden = false;
      }
      setStatus('');
    }

    function clearError() {
      if (els.error) els.error.hidden = true;
    }

    // Het maken duurt minuten; klanten wisselen ondertussen van tabblad.
    // Daarom melden we het resultaat in de tabtitel.
    var pageTitle = document.title;
    function notifyReadyInTab() {
      if (!document.hidden) return;
      document.title = '✔ Je 3D-beeldje is klaar!';
      var restore = function () {
        if (document.hidden) return;
        document.title = pageTitle;
        document.removeEventListener('visibilitychange', restore);
      };
      document.addEventListener('visibilitychange', restore);
    }

    /* ---------- voortgang ---------- */

    function paintProgress() {
      var pct = Math.max(0, Math.min(100, Math.round(state.progressShown)));
      if (els.progressBar) els.progressBar.style.width = pct + '%';
      if (els.progressText) els.progressText.textContent = pct + '%';
    }

    function startProgress() {
      state.progressTarget = 2;
      state.progressShown = 0;
      paintProgress();
      clearInterval(state.progressTimer);
      // Meshy meldt alleen bij elke poll een nieuw percentage; daartussen laten we
      // de balk rustig doorkruipen zodat het niet lijkt of de tool vastloopt.
      state.progressTimer = setInterval(function () {
        if (state.progressShown < state.progressTarget) {
          state.progressShown += Math.max(0.4, (state.progressTarget - state.progressShown) * 0.18);
        } else if (state.progressShown < 95) {
          state.progressShown += 0.12;
        }
        paintProgress();
      }, 500);
    }

    function setProgressTarget(pct) {
      if (typeof pct !== 'number' || isNaN(pct)) return;
      state.progressTarget = Math.max(state.progressTarget, Math.min(99, pct));
    }

    function stopProgress(finish) {
      clearInterval(state.progressTimer);
      state.progressTimer = null;
      if (finish) {
        state.progressShown = 100;
        paintProgress();
      }
    }

    /* ---------- api ---------- */

    // Foto inlezen. createImageBitmap houdt rekening met de EXIF-draaiing van
    // telefoonfoto's; lukt dat niet (oudere iOS), dan via een gewone <img>.
    async function decodeImage(file) {
      if (window.createImageBitmap) {
        try {
          return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
          try { return await createImageBitmap(file); } catch (e2) { /* door naar de <img>-route */ }
        }
      }
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var im = new Image();
        im.onload = function () { URL.revokeObjectURL(url); resolve(im); };
        im.onerror = function () {
          URL.revokeObjectURL(url);
          reject(new Error('dit fotobestand kunnen we niet openen (een HEIC-foto van een iPhone bijvoorbeeld). Kies een JPG- of PNG-foto'));
        };
        im.src = url;
      });
    }

    // Foto verkleinen naar een formaat dat door de backend past. Geeft een
    // data-URL (JPEG) terug die veilig onder de request-limiet van Vercel blijft.
    async function fileToUploadDataUrl(file) {
      var bitmap = await decodeImage(file);

      var srcW = bitmap.naturalWidth || bitmap.width;
      var srcH = bitmap.naturalHeight || bitmap.height;
      var scale = Math.min(1, MAX_UPLOAD_PX / Math.max(srcW, srcH));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(srcW * scale));
      canvas.height = Math.max(1, Math.round(srcH * scale));
      var ctx = canvas.getContext('2d');
      // Witte ondergrond: transparante PNG's worden anders zwart in JPEG.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      if (bitmap.close) bitmap.close();

      return canvasToUploadDataUrl(canvas);
    }

    // JPEG-kwaliteit terugschroeven tot het verzoek onder de limiet van de
    // backend blijft (Vercel weigert boven ~4,5 MB).
    function canvasToUploadDataUrl(canvas) {
      var quality = UPLOAD_QUALITY;
      var dataUrl = canvas.toDataURL('image/jpeg', quality);
      while (dataUrl.length > MAX_UPLOAD_CHARS && quality > 0.4) {
        quality -= 0.12;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      if (dataUrl.length > MAX_UPLOAD_CHARS) {
        // Extreem geval: nog een slag kleiner maken.
        var small = document.createElement('canvas');
        small.width = Math.max(1, Math.round(canvas.width * 0.6));
        small.height = Math.max(1, Math.round(canvas.height * 0.6));
        small.getContext('2d').drawImage(canvas, 0, 0, small.width, small.height);
        dataUrl = small.toDataURL('image/jpeg', 0.8);
      }
      return dataUrl;
    }

    async function api(path, opts) {
      var r;
      try {
        r = await fetch(cfg.apiBase + path, opts);
      } catch (e) {
        // Netwerkfout of geblokkeerde CORS — geeft anders alleen "Failed to fetch".
        throw new Error('we konden onze server niet bereiken, controleer je internetverbinding');
      }
      var data = await r.json().catch(function () { return {}; });
      if (!r.ok || data.ok === false) {
        if (r.status === 413) throw new Error('de foto was te groot om te versturen');
        throw new Error(data.message || data.error || 'HTTP ' + r.status);
      }
      return data;
    }

    async function pollUntilDone(taskId) {
      var deadline = Date.now() + cfg.pollMaxMs;
      while (Date.now() < deadline) {
        await new Promise(function (r) { setTimeout(r, POLL_INTERVAL_MS); });
        var st = await api('/image-to-3d-status?id=' + encodeURIComponent(taskId), { method: 'GET' });
        if (st.status === 'SUCCEEDED') return st;
        if (st.status === 'FAILED' || st.status === 'CANCELED') {
          throw new Error(st.message || 'de generatie is mislukt');
        }
        setProgressTarget(st.progress);
      }
      throw new Error('het duurde te lang (time-out)');
    }

    /* ---------- foto verwerken ---------- */

    /* ---------- foto's kiezen ---------- */

    function fileProblem(file) {
      if (file.type && file.type.indexOf('image/') !== 0) return 'Kies een foto (JPG of PNG).';
      if (file.size > MAX_FILE_BYTES) return 'Deze foto is groter dan 40 MB. Kies een kleinere foto.';
      return null;
    }

    function addFiles(list) {
      if (state.busy) return;
      var problem = null;
      Array.prototype.slice.call(list || []).forEach(function (file) {
        if (state.photos.length >= MAX_PHOTOS) {
          problem = 'Je kunt er maximaal ' + MAX_PHOTOS + ' foto’s bij doen.';
          return;
        }
        var p = fileProblem(file);
        if (p) { problem = p; return; }
        state.photos.push({ file: file, preview: URL.createObjectURL(file) });
      });

      if (els.file) els.file.value = '';
      if (problem) showError(problem); else clearError();

      if (state.photos.length) {
        renderThumbs();
        setState('chosen');
        // Het kader legt zelf al uit wat er kan; hier alleen tellen bij meerdere foto's.
        setStatus(state.photos.length > 1 ? state.photos.length + ' foto’s gekozen.' : '');
      }
    }

    function removePhoto(index) {
      var removed = state.photos.splice(index, 1)[0];
      if (removed) URL.revokeObjectURL(removed.preview);
      if (!state.photos.length) { resetTool(); return; }
      renderThumbs();
    }

    function renderThumbs() {
      if (!els.thumbs) return;
      els.thumbs.innerHTML = '';

      state.photos.forEach(function (photo, i) {
        var wrap = document.createElement('div');
        wrap.className = 'm3d__thumb';

        var img = document.createElement('img');
        img.src = photo.preview;
        img.alt = 'Gekozen foto ' + (i + 1);
        wrap.appendChild(img);

        var del = document.createElement('button');
        del.type = 'button';
        del.className = 'm3d__thumb-remove';
        del.setAttribute('aria-label', 'Foto ' + (i + 1) + ' verwijderen');
        del.textContent = '×';
        del.addEventListener('click', function () { removePhoto(i); });
        wrap.appendChild(del);

        els.thumbs.appendChild(wrap);
      });

      if (state.photos.length < MAX_PHOTOS) {
        var add = document.createElement('button');
        add.type = 'button';
        add.className = 'm3d__thumb-add';
        add.innerHTML = '<span>+</span>Hoek toevoegen';
        add.addEventListener('click', function () { if (els.file) els.file.click(); });
        els.thumbs.appendChild(add);
      }
    }

    /* ---------- inkaderen ---------- */

    // Staat het onderwerp klein in beeld, dan hertekent FLUX de foto niet maar
    // verzint hij een nieuw tafereel (gemeten: 2 personen -> 4 vreemden). Daarom
    // kadert de klant eerst zelf in, zodat de gezichten het kader vullen.
    var crop = { index: 0, zoom: 1, base: 1, x: 0, y: 0, dragging: false, lastX: 0, lastY: 0 };

    function startCropping() {
      if (state.busy || !state.photos.length) return;
      // Meteen aan het begin controleren: de voorstellen kosten ook geld, dus die
      // maken we niet voor iemand die daarna toch niet kan genereren.
      var wacht = usageWait(LIMITS.model);
      if (wacht) {
        showError('Je hebt net ' + LIMITS.model.max + ' beeldjes gemaakt. Over ' + wacht +
          ' minuten kun je er weer een maken. Heb je hulp nodig? Stuur ons een berichtje.');
        return;
      }
      if (!state.kinds.length || !state.subjects) {
        showError('Kies eerst wat er op de foto staat en hoeveel. Daar stemmen wij je beeldje op af.');
        if (els.kindBtns.length) els.kindBtns[0].closest('.m3d__card').scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      clearError();
      crop.index = 0;
      setState('cropping');
      loadCropPhoto();
    }

    function loadCropPhoto() {
      var photo = state.photos[crop.index];
      if (!photo || !els.cropImg) return;

      if (els.cropCount) {
        els.cropCount.textContent = state.photos.length > 1
          ? 'foto ' + (crop.index + 1) + ' van ' + state.photos.length
          : '';
      }
      if (els.cropOk) {
        els.cropOk.textContent = crop.index + 1 < state.photos.length ? 'Volgende foto' : 'Zo is het goed';
      }

      els.cropImg.onload = function () {
        var w = els.cropImg.naturalWidth;
        var h = els.cropImg.naturalHeight;
        // Het kader krijgt de verhouding van de foto zelf: een liggende foto geeft
        // een liggende uitsnede, een staande een staande. Een vast vierkant dwong
        // de klant eerder om er iets af te snijden, en juist bij een groepsfoto ging
        // daar de onderkant af, waardoor er geen voetje meer in beeld paste.
        crop.ratio = Math.min(MAX_CROP_RATIO, Math.max(1 / MAX_CROP_RATIO, w / h));
        // Breedte zelf uitrekenen in plaats van aan de CSS overlaten: bij een staande
        // foto zou een vaste breedte een kader opleveren dat niet meer op het scherm
        // past, en dan staat de knop eronder buiten beeld.
        var maxW = Math.min(320, Math.round(window.innerWidth * 0.78));
        var maxH = Math.round(window.innerHeight * 0.55);
        var fw = Math.round(Math.min(maxW, maxH * crop.ratio));
        els.cropFrame.style.width = fw + 'px';
        els.cropFrame.style.aspectRatio = crop.ratio + ' / 1';
        var fh = fw / crop.ratio;
        crop.base = Math.max(fw / w, fh / h);
        crop.zoom = 1;
        crop.x = 0;
        crop.y = 0;
        if (els.cropRange) els.cropRange.value = 100;
        renderCrop();
      };
      els.cropImg.src = photo.preview;
    }

    // Breedte en hoogte van het kader. De hoogte volgt uit de verhouding en niet uit
    // clientHeight, want zolang de stand nog niet zichtbaar is meet die 0.
    function frameSize() {
      var fw = els.cropFrame.clientWidth || 300;
      return { w: fw, h: fw / (crop.ratio || 1) };
    }

    function renderCrop() {
      if (!els.cropImg || !els.cropImg.naturalWidth) return;
      var dispW = els.cropImg.naturalWidth * crop.base * crop.zoom;
      els.cropImg.style.width = dispW + 'px';
      els.cropImg.style.height = 'auto';
      clampCrop();
      els.cropImg.style.transform =
        'translate(-50%, -50%) translate(' + crop.x + 'px, ' + crop.y + 'px)';

      // Hoeveel echte fotopixels zitten er in de uitsnede? Te weinig betekent een
      // vage, detailloze print, dus dat zeggen we meteen. De korte zijde telt: die
      // bepaalt hoeveel detail er overblijft.
      if (els.cropWarn) {
        var f = frameSize();
        var srcPx = Math.round(Math.min(f.w, f.h) * (els.cropImg.naturalWidth / dispW));
        els.cropWarn.hidden = srcPx >= MIN_CROP_PX;
      }
    }

    function clampCrop() {
      var f = frameSize();
      var dispW = els.cropImg.naturalWidth * crop.base * crop.zoom;
      var dispH = els.cropImg.naturalHeight * crop.base * crop.zoom;
      var maxX = Math.max(0, (dispW - f.w) / 2);
      var maxY = Math.max(0, (dispH - f.h) / 2);
      crop.x = Math.min(maxX, Math.max(-maxX, crop.x));
      crop.y = Math.min(maxY, Math.max(-maxY, crop.y));
    }

    function setZoom(pct) {
      crop.zoom = Math.min(4, Math.max(1, pct / 100));
      if (els.cropRange) els.cropRange.value = Math.round(crop.zoom * 100);
      renderCrop();
    }

    // Het zichtbare deel uit de bronfoto snijden op printwaardige resolutie, in de
    // verhouding van het kader.
    function cropToDataUrl() {
      var f = frameSize();
      var w = els.cropImg.naturalWidth;
      var h = els.cropImg.naturalHeight;
      var dispW = w * crop.base * crop.zoom;
      var perCssPx = w / dispW;

      // Nooit meer opvragen dan er is: de uitsnede past altijd binnen de bronfoto.
      var srcW = Math.min(w, f.w * perCssPx);
      var srcH = Math.min(h, f.h * perCssPx);
      var schaal = Math.min(1, w / srcW, h / srcH);
      srcW *= schaal;
      srcH *= schaal;

      var cx = w / 2 - crop.x * perCssPx;
      var cy = h / 2 - crop.y * perCssPx;
      var sx = Math.max(0, Math.min(w - srcW, cx - srcW / 2));
      var sy = Math.max(0, Math.min(h - srcH, cy - srcH / 2));

      // Omlaag als de uitsnede groot is, omhoog als hij erg klein is. Beide zijden
      // met dezelfde factor, anders wordt het beeldje uitgerekt.
      var uit = Math.min(1, MAX_UPLOAD_PX / Math.max(srcW, srcH));
      uit = Math.max(uit, 256 / Math.min(srcW, srcH));
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(srcW * uit);
      canvas.height = Math.round(srcH * uit);
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(els.cropImg, sx, sy, srcW, srcH, 0, 0, canvas.width, canvas.height);
      return canvasToUploadDataUrl(canvas);
    }

    function confirmCrop() {
      var photo = state.photos[crop.index];
      if (!photo) return;
      try {
        photo.cropped = cropToDataUrl();
      } catch (err) {
        console.error('[meshy3d] inkaderen mislukt, hele foto gebruiken', err);
        photo.cropped = null;
      }
      crop.index++;
      if (crop.index < state.photos.length) {
        loadCropPhoto();
      } else {
        startGeneration();
      }
    }

    /* ---------- 3D-beeldje maken ---------- */

    async function startGeneration() {
      if (state.busy || !state.photos.length) return;

      if (!cfg.apiBase || !cfg.widgetToken) {
        showError('De 3D-tool is nog niet volledig ingesteld. Neem even contact met ons op, dan helpen we je verder.');
        return;
      }

      state.busy = true;
      state.stlUrl = state.glbUrl = state.thumbUrl = null;
      clearError();
      if (els.addBtn) els.addBtn.disabled = true;

      try {
        setState('working');
        startProgress();
        if (els.sourcePreview) els.sourcePreview.src = state.photos[0].preview;

        var urls = [];
        for (var i = 0; i < state.photos.length; i++) {
          setStatus(state.photos.length > 1
            ? 'Foto ' + (i + 1) + ' van ' + state.photos.length + ' uploaden…'
            : 'Je foto wordt klaargemaakt…');
          var dataUrl = state.photos[i].cropped || await fileToUploadDataUrl(state.photos[i].file);
          var up = await api('/upload-image', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image_base64: dataUrl,
              widget_token: cfg.widgetToken,
              filename: 'meshy-bron-' + (i + 1) + '.jpg'
            })
          });
          urls.push(up.image_url);
        }
        state.photoUrls = urls;
        state.photoUrl = urls[0];
        state.busy = false;

        // Direct het 3D-model maken. De backend maakt van de foto zelf een schone
        // referentie; de klant beoordeelt het resultaat aan het draaibare model, en
        // dat is de controle die telt: hij bestelt pas als dat bevalt.
        await runMeshy({});
      } catch (err) {
        state.busy = false;
        failBack(err);
      }
    }

    /* ---------- 3D-model maken ---------- */

    async function runMeshy(extra) {
      if (state.busy) return;
      var wachtModel = usageWait(LIMITS.model);
      if (wachtModel) {
        showError('Je hebt net ' + LIMITS.model.max + ' beeldjes gemaakt. Over ' + wachtModel +
          ' minuten kun je er weer een maken. Heb je hulp nodig? Stuur ons een berichtje.');
        return;
      }
      state.busy = true;
      state.stlUrl = state.glbUrl = state.thumbUrl = null;
      clearError();
      if (els.addBtn) els.addBtn.disabled = true;

      try {
        setState('working');
        startProgress();
        if (els.sourcePreview) {
          els.sourcePreview.src = extra.reference_url ||
            (state.photos[0] ? state.photos[0].preview : '');
        }
        // De voortgangsbalk vertelt zelf wat er gebeurt.
        setStatus('');

        var payload = {
          // image_url blijft meegaan zodat een oudere backend ook werkt.
          image_url: state.photoUrls[0],
          image_urls: state.photoUrls,
          widget_token: cfg.widgetToken,
          subjects: state.subjects,
          // Komma-gescheiden; de backend accepteert ook nog een enkele waarde.
          kind: state.kinds.join(',')
        };
        if (extra.reference_url) payload.reference_url = extra.reference_url;

        var sub = await api('/image-to-3d', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        usageNote(LIMITS.model);
        // Het beeld waar het model op gebaseerd is, bewaren we bij de order.
        state.referenceUrl = extra.reference_url || sub.prepped_image_url || null;

        var result = await pollUntilDone(sub.task_id);
        state.stlUrl = result.stl_url;
        state.glbUrl = result.preview_glb_url;
        state.thumbUrl = result.thumbnail_url;

        stopProgress(true);
        renderPreview(result);
        setState('done');
        setStatus('Klaar. Je kunt hem nu in je winkelwagen leggen.');
        if (els.addBtn) els.addBtn.disabled = false;
        notifyReadyInTab();
      } catch (err) {
        failBack(err);
      } finally {
        state.busy = false;
      }
    }

    // Terug naar de vorige zinnige stand, met de foto's van de klant intact.
    function failBack(err) {
      console.error('[meshy3d]', err);
      stopProgress(false);
      if (state.photos.length) {
        renderThumbs();
        setState('chosen');
      } else {
        setState('empty');
      }
      if (els.file) els.file.value = '';
      showError('Het is niet gelukt: ' + err.message + '. Probeer het opnieuw of kies een andere foto.');
    }

    function renderPreview(result) {
      if (!els.viewer) return;
      els.viewer.innerHTML = '';

      if (result.preview_glb_url && window.customElements) {
        var mv = document.createElement('model-viewer');
        // Lukt het 3D-model niet (oud toestel, geen WebGL), dan tonen we de
        // afbeelding — de klant moet altijd íets van zijn beeldje zien.
        mv.addEventListener('error', function () { renderThumbnailFallback(result); });
        mv.setAttribute('src', result.preview_glb_url);
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('auto-rotate', '');
        mv.setAttribute('touch-action', 'pan-y');
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('exposure', '1');
        mv.setAttribute('alt', '3D-beeldje van jouw foto');
        mv.addEventListener('load', function () { applyColor(mv); });
        els.viewer.appendChild(mv);
      } else {
        renderThumbnailFallback(result);
      }
    }

    function renderThumbnailFallback(result) {
      if (!els.viewer || !result.thumbnail_url) return;
      els.viewer.innerHTML = '';
      var img = document.createElement('img');
      img.src = result.thumbnail_url;
      img.alt = '3D-beeldje van jouw foto';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      els.viewer.appendChild(img);
    }

    /* ---------- kleur ---------- */

    function applyColor(mv) {
      mv = mv || (els.viewer && els.viewer.querySelector('model-viewer'));
      if (!mv || !mv.model) return;
      var c = COLORS[state.color] || COLORS[DEFAULT_COLOR];
      var rgba = [c.rgb[0], c.rgb[1], c.rgb[2], 1];
      (mv.model.materials || []).forEach(function (m) {
        try {
          m.pbrMetallicRoughness.setBaseColorFactor(rgba);
          m.pbrMetallicRoughness.setMetallicFactor(0);
          m.pbrMetallicRoughness.setRoughnessFactor(0.85);
        } catch (e) { /* materiaal ondersteunt dit niet — preview blijft zoals hij is */ }
      });
    }

    function setColor(key) {
      if (!COLORS[key]) return;
      // Een kleur die tijdelijk niet leverbaar is, staat in de liquid op disabled.
      // De browser blokkeert de klik al, maar deze controle vangt ook een aanroep
      // uit de code af (bijvoorbeeld het herstellen van een eerdere keuze).
      var knop = null;
      els.colorBtns.forEach(function (b) { if (b.dataset.color === key) knop = b; });
      if (knop && knop.disabled) return;
      state.color = key;
      els.colorBtns.forEach(function (b) {
        var on = b.dataset.color === key;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      applyColor();
    }

    /* ---------- aantal personen/dieren & maatadvies ---------- */

    // Aan/uit per soort: de klant tikt alles aan wat er op de foto staat.
    function toggleKind(kind) {
      var i = state.kinds.indexOf(kind);
      if (i === -1) state.kinds.push(kind); else state.kinds.splice(i, 1);
      els.kindBtns.forEach(function (b) {
        var on = state.kinds.indexOf(b.dataset.kind) !== -1;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function setSubjects(n) {
      state.subjects = n;
      if (els.cropOk) els.cropOk.addEventListener('click', confirmCrop);
    if (els.cropRange) {
      els.cropRange.addEventListener('input', function () { setZoom(parseInt(els.cropRange.value, 10) || 100); });
    }
    els.cropZoomBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        setZoom(Math.round(crop.zoom * 100) + (b.dataset.zoom === 'in' ? 25 : -25));
      });
    });
    if (els.cropFrame) {
      // Twee vingers = knijpzoomen, één vinger of de muis = slepen.
      var pointers = new Map();
      var pinchStart = null;

      function pinchDistance() {
        var pts = Array.from(pointers.values());
        return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      }

      els.cropFrame.addEventListener('pointerdown', function (e) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        els.cropFrame.setPointerCapture(e.pointerId);
        if (pointers.size === 2) {
          pinchStart = { dist: pinchDistance(), zoom: crop.zoom };
          crop.dragging = false;
        } else if (pointers.size === 1) {
          crop.dragging = true;
          crop.lastX = e.clientX;
          crop.lastY = e.clientY;
          els.cropFrame.classList.add('is-dragging');
        }
      });
      els.cropFrame.addEventListener('pointermove', function (e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointers.size >= 2 && pinchStart) {
          var factor = pinchDistance() / (pinchStart.dist || 1);
          setZoom(Math.round(pinchStart.zoom * factor * 100));
          return;
        }
        if (!crop.dragging) return;
        crop.x += e.clientX - crop.lastX;
        crop.y += e.clientY - crop.lastY;
        crop.lastX = e.clientX;
        crop.lastY = e.clientY;
        renderCrop();
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        els.cropFrame.addEventListener(ev, function (e) {
          pointers.delete(e.pointerId);
          if (pointers.size < 2) pinchStart = null;
          if (pointers.size === 0) {
            crop.dragging = false;
            els.cropFrame.classList.remove('is-dragging');
          }
        });
      });
      els.cropFrame.addEventListener('wheel', function (e) {
        e.preventDefault();
        setZoom(Math.round(crop.zoom * 100) + (e.deltaY < 0 ? 10 : -10));
      }, { passive: false });
    }
    els.subjectBtns.forEach(function (b) {
        var on = parseInt(b.dataset.subjects, 10) === n;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      updateSizeAdvice();
    }

    // Bij meerdere figuren op hetzelfde voetje valt elk figuur kleiner uit. Dat
    // zegt de tool er nu bij, inclusief een advies welk formaat dan beter past.
    function updateSizeAdvice() {
      if (els.scale) els.scale.hidden = !state.subjects || state.subjects < 2;
      if (!els.sizeAdvice) return;

      var inputs = Array.prototype.slice.call(els.variantInputs);
      if (!state.subjects || state.subjects < 2 || inputs.length < 2) {
        els.sizeAdvice.hidden = true;
        return;
      }

      var chosen = 0;
      inputs.forEach(function (input, i) { if (input.checked) chosen = i; });
      // Bij 2 figuren minstens de middelste maat, bij 3 of meer de grootste.
      var advised = state.subjects >= 3 ? inputs.length - 1 : 1;
      var aantal = state.subjects >= 3 ? '3 of meer' : '2';
      var chosenTitle = inputs[chosen] ? inputs[chosen].dataset.title : '';
      var advisedTitle = inputs[advised] ? inputs[advised].dataset.title : '';

      if (chosen < advised) {
        // "of groter" alleen als er ook echt nog een grotere maat bestaat.
        var groter = advised < inputs.length - 1 ? ' of groter' : '';
        els.sizeAdvice.innerHTML = '<strong>Let op:</strong> met ' + aantal +
          ' op de foto staan ze samen op hetzelfde voetje, dus wordt elk figuur kleiner. In ' +
          esc(chosenTitle) + ' blijft er weinig detail over — wij raden ' + esc(advisedTitle) +
          groter + ' aan.';
      } else {
        els.sizeAdvice.innerHTML = 'Goede keuze: in ' + esc(chosenTitle) + ' is bij ' + aantal +
          ' op de foto genoeg ruimte voor detail.';
      }
      els.sizeAdvice.hidden = false;
    }

    function esc(text) {
      var d = document.createElement('span');
      d.textContent = text || '';
      return d.innerHTML;
    }

    /* ---------- variant (formaat) ---------- */

    function setVariant(input) {
      state.variantId = parseInt(input.value, 10) || state.variantId;
      state.variantTitle = input.dataset.title || '';
      els.variantInputs.forEach(function (other) {
        var label = other.closest('.m3d__pill');
        if (label) label.classList.toggle('m3d__pill--checked', other === input);
      });
      if (els.price && input.dataset.price) els.price.textContent = input.dataset.price;
      if (els.priceCompare) {
        var cmp = input.dataset.compare || '';
        els.priceCompare.textContent = cmp;
        els.priceCompare.hidden = cmp === '';
      }
      updateSizeAdvice();
    }

    /* ---------- winkelwagen ---------- */

    async function addToCart() {
      if (!state.stlUrl || state.busy) return;
      if (!state.variantId) {
        showError('Geen product-variant gevonden om aan de winkelwagen toe te voegen.');
        return;
      }

      var properties = {
        Kleur: (COLORS[state.color] || COLORS[DEFAULT_COLOR]).label,
        'STL-bestand (printen)': state.stlUrl,
        _preview: state.thumbUrl || state.glbUrl || ''
      };
      if (state.variantTitle) properties.Formaat = state.variantTitle;
      if (state.kinds.length && state.subjects) {
        properties['Op de foto'] = state.kinds.map(function (k) { return KIND_LABELS[k]; }).join(' + ') +
          ', ' + (state.subjects >= 3 ? '3 of meer' : String(state.subjects));
      }
      if (state.glbUrl) properties['3D-model (preview)'] = state.glbUrl;
      state.photoUrls.forEach(function (url, i) {
        properties[i === 0 ? 'Klantfoto (origineel)' : 'Klantfoto ' + (i + 1)] = url;
      });
      if (state.referenceUrl) properties['Referentiebeeld (AI)'] = state.referenceUrl;

      var items = [{ id: state.variantId, quantity: 1, properties: properties }];

      if (els.giftCheck && els.giftCheck.checked) {
        var giftId = parseInt(els.giftCheck.dataset.variantId, 10);
        if (giftId && giftId !== state.variantId) items.push({ id: giftId, quantity: 1 });
      }

      var origText = els.addBtn ? els.addBtn.textContent : '';
      if (els.addBtn) {
        els.addBtn.disabled = true;
        els.addBtn.textContent = 'Toevoegen…';
      }

      try {
        var r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!r.ok) throw new Error((await r.text()).substring(0, 200));

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));

        if (els.addBtn) els.addBtn.textContent = 'Toegevoegd ✓';
        setTimeout(function () { window.location.href = '/cart'; }, 1200);
      } catch (err) {
        showError('Toevoegen mislukt: ' + err.message);
        if (els.addBtn) {
          els.addBtn.disabled = false;
          els.addBtn.textContent = origText;
        }
      }
    }

    /* ---------- reset ---------- */

    function resetTool() {
      stopProgress(false);
      state.photos.forEach(function (p) { URL.revokeObjectURL(p.preview); });
      state.photos = [];
      state.photoUrls = [];
      if (els.thumbs) els.thumbs.innerHTML = '';
      state.stlUrl = state.glbUrl = state.thumbUrl = state.photoUrl = state.referenceUrl = null;
      state.progressTarget = state.progressShown = 0;
      paintProgress();
      if (els.viewer) els.viewer.innerHTML = '';
      if (els.sourcePreview) els.sourcePreview.removeAttribute('src');
      if (els.file) els.file.value = '';
      if (els.addBtn) {
        els.addBtn.disabled = true;
        els.addBtn.textContent = els.addBtn.dataset.label || els.addBtn.textContent;
      }
      clearError();
      setState('empty');
      setStatus('Kies eerst een foto.');
    }

    /* ---------- events ---------- */

    if (els.file) {
      els.file.addEventListener('change', function (e) { addFiles(e.target.files); });
    }
    if (els.generateBtn) els.generateBtn.addEventListener('click', startCropping);
    els.clearBtns.forEach(function (b) { b.addEventListener('click', resetTool); });
    if (els.cropOk) els.cropOk.addEventListener('click', confirmCrop);
    if (els.cropRange) {
      els.cropRange.addEventListener('input', function () { setZoom(parseInt(els.cropRange.value, 10) || 100); });
    }
    els.cropZoomBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        setZoom(Math.round(crop.zoom * 100) + (b.dataset.zoom === 'in' ? 25 : -25));
      });
    });
    if (els.cropFrame) {
      // Twee vingers = knijpzoomen, één vinger of de muis = slepen.
      var pointers = new Map();
      var pinchStart = null;

      function pinchDistance() {
        var pts = Array.from(pointers.values());
        return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      }

      els.cropFrame.addEventListener('pointerdown', function (e) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        els.cropFrame.setPointerCapture(e.pointerId);
        if (pointers.size === 2) {
          pinchStart = { dist: pinchDistance(), zoom: crop.zoom };
          crop.dragging = false;
        } else if (pointers.size === 1) {
          crop.dragging = true;
          crop.lastX = e.clientX;
          crop.lastY = e.clientY;
          els.cropFrame.classList.add('is-dragging');
        }
      });
      els.cropFrame.addEventListener('pointermove', function (e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointers.size >= 2 && pinchStart) {
          var factor = pinchDistance() / (pinchStart.dist || 1);
          setZoom(Math.round(pinchStart.zoom * factor * 100));
          return;
        }
        if (!crop.dragging) return;
        crop.x += e.clientX - crop.lastX;
        crop.y += e.clientY - crop.lastY;
        crop.lastX = e.clientX;
        crop.lastY = e.clientY;
        renderCrop();
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        els.cropFrame.addEventListener(ev, function (e) {
          pointers.delete(e.pointerId);
          if (pointers.size < 2) pinchStart = null;
          if (pointers.size === 0) {
            crop.dragging = false;
            els.cropFrame.classList.remove('is-dragging');
          }
        });
      });
      els.cropFrame.addEventListener('wheel', function (e) {
        e.preventDefault();
        setZoom(Math.round(crop.zoom * 100) + (e.deltaY < 0 ? 10 : -10));
      }, { passive: false });
    }
    els.subjectBtns.forEach(function (b) {
      b.addEventListener('click', function () { setSubjects(parseInt(b.dataset.subjects, 10) || 1); });
    });
    els.kindBtns.forEach(function (b) {
      b.addEventListener('click', function () { toggleKind(b.dataset.kind || 'persoon'); });
    });

    if (els.stage) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        els.stage.addEventListener(ev, function (e) {
          if (root.dataset.state !== 'empty' && root.dataset.state !== 'chosen') return;
          e.preventDefault();
          root.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        els.stage.addEventListener(ev, function () { root.classList.remove('is-dragover'); });
      });
      els.stage.addEventListener('drop', function (e) {
        if (root.dataset.state !== 'empty' && root.dataset.state !== 'chosen') return;
        e.preventDefault();
        addFiles(e.dataTransfer && e.dataTransfer.files);
      });
    }

    if (els.addBtn) {
      els.addBtn.dataset.label = els.addBtn.textContent.trim();
      els.addBtn.addEventListener('click', addToCart);
    }
    if (els.resetBtn) {
      els.resetBtn.addEventListener('click', function () {
        resetTool();
        if (els.file) els.file.click();
      });
    }

    els.colorBtns.forEach(function (b) {
      b.addEventListener('click', function () { setColor(b.dataset.color); });
    });
    els.variantInputs.forEach(function (input) {
      input.addEventListener('change', function () { setVariant(input); });
      if (input.checked) setVariant(input);
    });

    setColor(DEFAULT_COLOR);
    updateSizeAdvice();
    resetTool();
  }

  function boot() {
    document.querySelectorAll('.m3d').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
